import React from 'react';
import { stripLeadingDecorators } from './types';

interface MarkdownInlineProps {
    text: string;
}

type TagKind = 'perda' | 'canal' | 'usuario';

type InlinePart =
    | { type: 'text'; value: string }
    | { type: 'strong'; value: string }
    | { type: 'tag'; kind: TagKind; value: string };

type RenderGroup =
    | { type: 'text'; value: string }
    | { type: 'strong'; value: string }
    | { type: 'tags'; items: Extract<InlinePart, { type: 'tag' }>[] };

const TAG_RE = /\{\{(perda|canal|usuario)\|([^}]+)\}\}/gi;

const TAG_CLASS: Record<TagKind, string> = {
    perda: 'badge badge-sm badge-danger',
    canal: 'badge badge-sm badge-info',
    usuario: 'badge badge-sm badge-brand',
};

function toTagKind(raw: string): TagKind | null {
    const k = raw.trim().toLowerCase();
    if (k === 'perda') return 'perda';
    if (k === 'canal') return 'canal';
    if (k === 'usuario') return 'usuario';
    return null;
}

function splitStrong(text: string): InlinePart[] {
    return text
        .split(/(\*\*.*?\*\*)/g)
        .filter(Boolean)
        .map((part) =>
            part.startsWith('**') && part.endsWith('**')
                ? { type: 'strong' as const, value: part.slice(2, -2) }
                : { type: 'text' as const, value: part }
        );
}

function parseInline(text: string): InlinePart[] {
    const cleaned = stripLeadingDecorators(text);
    const parts: InlinePart[] = [];
    const re = new RegExp(TAG_RE.source, 'gi');
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = re.exec(cleaned)) !== null) {
        if (match.index > last) {
            parts.push(...splitStrong(cleaned.slice(last, match.index)));
        }
        const kind = toTagKind(match[1]);
        const value = match[2].trim();
        if (kind && value) {
            parts.push({ type: 'tag', kind, value });
        } else if (value) {
            parts.push({ type: 'text', value });
        }
        last = match.index + match[0].length;
    }

    if (last < cleaned.length) {
        parts.push(...splitStrong(cleaned.slice(last)));
    }

    return parts;
}

/** Agrupa tags consecutivas; whitespace entre elas vira gap-s2. */
function groupParts(parts: InlinePart[]): RenderGroup[] {
    const groups: RenderGroup[] = [];

    for (const part of parts) {
        if (part.type === 'tag') {
            const prev = groups[groups.length - 1];
            if (prev?.type === 'tags') {
                prev.items.push(part);
            } else {
                groups.push({ type: 'tags', items: [part] });
            }
            continue;
        }

        if (part.type === 'text' && !part.value.trim()) {
            if (groups[groups.length - 1]?.type === 'tags') continue;
        }

        groups.push(part);
    }

    return groups;
}

export const MarkdownInline: React.FC<MarkdownInlineProps> = ({ text }) => {
    if (!text) return null;

    const groups = groupParts(parseInline(text));

    return (
        <>
            {groups.map((group, i) => {
                if (group.type === 'tags') {
                    return (
                        <span
                            key={i}
                            className="mx-s1 inline-flex max-w-full flex-wrap items-center gap-s2 align-middle"
                            role="list"
                            aria-label="Tags da análise"
                        >
                            {group.items.map((tag, j) => (
                                <span
                                    key={j}
                                    className={TAG_CLASS[tag.kind]}
                                    role="listitem"
                                    aria-label={`${tag.kind}: ${tag.value}`}
                                >
                                    {tag.value}
                                </span>
                            ))}
                        </span>
                    );
                }
                if (group.type === 'strong') {
                    return (
                        <strong key={i} className="font-semibold text-default">
                            {group.value}
                        </strong>
                    );
                }
                return <span key={i}>{group.value}</span>;
            })}
        </>
    );
};
